import React, { useState } from 'react';
import PropTypes from 'prop-types';
import camelize from 'camelize';
import queryString from 'query-string';
import { urls } from 'app-constants';
import { pluralize } from 'utils';
import Badge from 'components/Badge';
import LoadingOverlay from 'components/LoadingOverlay';
import Filters from './Filters';
import ResultItem from './ResultItem';

const PoiDuplicateReport = ({ countyChoices, mapboxToken }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [reportData, setReportData] = useState(null);

  const handleRunReport = filterParams => {
    const queryParams = Object.entries(filterParams).reduce((result, [key, value]) => {
      if (value) result[key] = value;
      return result;
    }, {});
    const url = `${urls.poiDuplicateReportData}?${queryString.stringify(queryParams)}`;

    setIsFetching(true);
    fetch(url, { credentials: 'include' })
      .then(response => {
        setIsFetching(false);
        if (!response.ok) throw new Error(response.statusText);
        return response;
      })
      .then(response => response.json())
      .then(data => setReportData(camelize(data)))
      .catch(err => {
        setIsFetching(false);
        console.error(err);
      });
  };

  const handleResolve = poiIds => setReportData(os => ({
    ...os,
    results: os.results.filter(pois => !pois.every(p => poiIds.includes(p.id))),
  }));

  let curParams = [];
  let mapZoom = 16;
  if (reportData) {
    const { radius, county, nameThreshold, streetThreshold } = reportData;
    if (county) curParams.push(county);
    if (radius) curParams.push(`< ${radius} meters`);
    if (nameThreshold) curParams.push(`name ${nameThreshold}%`);
    if (streetThreshold) curParams.push(`address ${streetThreshold}%`);

    if (radius >= 150) mapZoom = 15;
    if (radius >= 200) mapZoom = 14;
    if (radius >= 500) mapZoom = 12;
  }

  const keyFromResult = pois => pois.map(p => p.id).join('_');

  return (
    <div>
      <div className="mb-4">
        <Filters
          countyChoices={countyChoices}
          canSubmit={!isFetching}
          onSubmit={handleRunReport}
        />
        <hr />
        <div style={{ position: 'relative', minHeight: 200 }}>
          <LoadingOverlay show={isFetching} align="top" />
          {!reportData ? (
            <div className="text-secondary"><em>Configure the filter parameters as desired and select "Run Report."</em></div>
          ) : (
            <div>
              <div className="d-flex align-items-center mb-4" style={{ gap: 15 }}>
                <strong>{pluralize(reportData.results.length, 'result')} for:</strong>
                {curParams.map(val => <Badge key={val} text={val} colorClass="standard" />)}
              </div>

              {reportData.results.map(pois => (
                <ResultItem
                  key={keyFromResult(pois)}
                  pois={pois}
                  mapboxToken={mapboxToken}
                  mapZoom={mapZoom}
                  onResolveSuccess={handleResolve}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PoiDuplicateReport.propTypes = {
  countyChoices: PropTypes.array,
  mapboxToken: PropTypes.string,
};

export default PoiDuplicateReport;
